import type { EnemyKind } from './archetypes';

export interface Habitat {
  id: string;
  kinds: EnemyKind[];
  area: { minX: number; maxX: number; minZ: number; maxZ: number };
  /**
   * A world-Y close to this habitat's real ground/floor height — resolves
   * `BlockPhysics.groundHeightAt`'s tolerance-gated ambiguity between
   * overlapping floor regions (street vs. rooftop can both cover the same
   * XZ) at bake time. Also the baseline a flyer archetype's `flightHeight`
   * is added onto (ai/enemy.ts) — this is what makes a rooftop pigeon hover
   * near the rooftop's own height instead of the street's.
   */
  groundHint: number;
  maxPopulation: number;
}

/**
 * The S01 gray Astoria block's population, as habitat ZONES rather than a
 * fixed list of individual spawn points (S02's own former shape) — S03 scope
 * per PLAN-CITY §5. Loosely follows C02's habitat map board (rats in the
 * alley, pigeons on rooftops/in the park, trash lobbers on curbs, the Big
 * Rat/Rat-King-standin roaming a patrol loop) adapted onto THIS gray block's
 * actual named zones from world/layout.ts, since the real Astoria block
 * (C04/S09) doesn't exist yet. Every area here is hand-checked against
 * `BUILDINGS`/`ALLEY`/`ROOFTOP` in layout.ts to stay clear of solid geometry
 * — a habitat rectangle that overlaps a wall would occasionally roll a spawn
 * point stuck inside it.
 */
export const HABITATS: Habitat[] = [
  // Building A/B's alley — rats' natural cover. NOTE: `ALLEY` in layout.ts
  // (x15-19) spans a wider box than the ground-level alley actually is —
  // `FIRE_ESCAPE_RAMP` (x15-17) and its rail (x17-17.4) occupy the WEST
  // half of that box, and `BUILDINGS.building_b`'s west wall sits at
  // x19-19.4 on the east side. The genuinely walkable street-level strip is
  // only x~17.4-19, so this habitat is deliberately narrow (with a small
  // jitter, see `SPAWN_JITTER` below) rather than reusing ALLEY's full
  // width — see the harness-gotcha note at the bottom of this file for why.
  { id: 'alley', kinds: ['rat', 'pizza_rat'], area: { minX: 17.9, maxX: 18.5, minZ: 12, maxZ: 25 }, groundHint: 0, maxPopulation: 2 },
  // NW quadrant park pocket — open ground, planters/benches double as both vault obstacles and light cover.
  { id: 'park_pocket', kinds: ['trash_lobber', 'rat'], area: { minX: -24, maxX: -8, minZ: 8, maxZ: 26 }, groundHint: 0, maxPopulation: 2 },
  // Open pavement south of the intersection, east side — kept clear of the
  // subway pit's own footprint (x-8..-3) with a real margin (SUBWAY_STAIR/
  // SUBWAY_LANDING both start at x-8; this area starts at x0, 8m clear).
  { id: 'street_curbs', kinds: ['trash_lobber'], area: { minX: 0, maxX: 6, minZ: -22, maxZ: -10 }, groundHint: 0, maxPopulation: 1 },
  // Wave D-1 (PLAN-CITY2 §4): the `elite_patrol` open-roam Rat King habitat
  // is RETIRED — the real fight now lives inside the basement lair
  // (world/denLair.ts, world/dens.ts's `LairEncounter`), hosting the real
  // `RatKingBoss` (ai/ratking.ts) instead of a roaming `Enemy`. Deleted, not
  // kept dead alongside a replacement entry (same "elite_rat itself is
  // deleted, not kept dead" precedent this exact comment used to cite for
  // S10's own predecessor swap) — no habitat slot may ever produce a
  // `rat_king`-kind `Enemy` again. `ELITE_RESPAWN_DELAY`/`ai/spawner.ts`'s
  // own frustum on-screen gate were built ONLY for this entry; both are
  // retired alongside it (see those files' own Wave D comments).
  // Low-altitude pigeons over the park — flightHeight is added onto groundHint 0, so these hover near street level per C03's own ~1.2-1.6m spec.
  { id: 'park_airspace', kinds: ['pigeon', 'dive_pigeon'], area: { minX: -22, maxX: -10, minZ: 10, maxZ: 24 }, groundHint: 0, maxPopulation: 2 },
  // High above Building A's own roof deck — only reachable/engageable once the player has actually climbed the fire escape (S01's own headline traversal feature gets something to fight up there).
  { id: 'rooftop_airspace', kinds: ['pigeon'], area: { minX: 9, maxX: 14, minZ: 10, maxZ: 24 }, groundHint: 16, maxPopulation: 1 },
  // S11: the golden_pigeon rare-roamer stub — deliberately reuses
  // park_airspace's EXACT bounds rather than authoring a new rectangle: that
  // area is already proven clear of every non-street FloorRegion (this
  // file's own long-standing bottom comment on why that check matters), so
  // this needed zero fresh geometry validation. Rarity comes entirely from
  // the archetype's own long `respawnDelay` (ai/archetypes.ts), not from
  // this habitat's shape — a normal `maxPopulation:1` slot like any other.
  { id: 'golden_roamer', kinds: ['golden_pigeon'], area: { minX: -22, maxX: -10, minZ: 10, maxZ: 24 }, groundHint: 0, maxPopulation: 1 },
  // Wave E (PLAN-CITY3 §6): the el platform, deck height 7.4 (layout.ts's
  // EL_DECK_WALK_Y — hardcoded here, matching this file's own established
  // style of plain numbers cross-checked by comment rather than a fresh
  // cross-file import). Both-direction cross-check: this rectangle
  // (x[3,23], z[-1.6,1.6]) sits entirely inside EL_DECK_FLOOR's own real
  // bounds (x[-16,32], z[-2,2]) with real margin on every edge — clear of
  // the train/nose corridor (x<0), the station stair's gap (x26.5-29.5),
  // and the bumper/vantage/collectible cluster further east (x>=29). The
  // reverse direction needs no check here: `pigeon` is a pure flyer
  // (movementMode!=='ground'), so it never calls `snapToGround`/collides
  // with walls (ai/enemy.ts) — its absolute hover altitude is always
  // `groundHint + flightHeight` (7.4+1.2=8.6), comfortably inside
  // EL_DECK_RAILS' own height band (6.9-9.06), never touching street level
  // 34m below even though this XZ band also overlaps STREET_FLOOR.
  { id: 'el_platform_airspace', kinds: ['pigeon'], area: { minX: 3, maxX: 23, minZ: -1.6, maxZ: 1.6 }, groundHint: 7.4, maxPopulation: 1 },
];

// A durable lesson for any FUTURE habitat placement in this file (worth
// re-reading before Wave C's real Astoria block, C04/S09, adds many more):
// `PlayerCapsule.teleport(x,z)` deliberately uses a HUGE tolerance
// (world/controller.ts: `groundHeightAt(x,z,50,200)`, "land on whatever's
// below, any level") — correct for its own job (the dev-tap teleport should
// be able to drop the player onto a rooftop it taps), but it means ANY
// habitat rectangle that geometrically overlaps a ramp/rooftop/tunnel/
// subway floor region's XZ footprint will teleport-test code (and the dev
// teleport itself) onto THAT region's height, not street level — even
// though a live GROUND ENEMY standing at the same XZ stays correctly
// grounded at street level (`Enemy.snapToGround`'s `groundHeightAt` call
// uses a much smaller, CURRENT-height-relative tolerance, 0.6m, which
// excludes a ramp/rooftop region once its height is more than 0.6m above
// wherever the enemy already is). This bit S03 for real: the alley's first
// draft (x15.5-18.5) overlapped `FIRE_ESCAPE_RAMP`'s own footprint
// (x15-17) for over half its width, and the harness's own long-standing
// `player.teleport(ratX - 1.5, ratZ)` pattern (safe for S02's isolated,
// building-clear spawn points) silently landed the TEST PLAYER on the ramp
// or the rooftop while the RAT it was testing against sat untouched at
// street level 8-16m below — two harness checks failed with confusing
// "shots fired, target not killed" / "telegraph never seen" symptoms that
// had nothing to do with combat logic at all. Before adding or widening a
// ground-level habitat, check its rectangle against every non-street
// FloorRegion in world/layout.ts (FIRE_ESCAPE_RAMP + its rail, ROOFTOP,
// ROOFTOP_LANDING, TUNNEL_RAMP/FLOOR, SUBWAY_STAIR/LANDING) with a real
// margin, not just against BUILDINGS' wall boxes.
